import React, { useEffect } from 'react';
import closeIcon from "../../assets/close-icon.png"
import checkIcon from "../../assets/check-filled-icon.png"
import { useDispatch, useSelector } from 'react-redux';
import { 
  resetDocumentsState, 
  resetSelectedSender, 
  resetSelectedReceiver, 
  resetSelectedAgent, 
  clearTaricJsonErrors,
  updateWorkflowSessionName,
  updateWorkflowSessionState,
  resetTabs,
  setExtractedData,
  setEditableData,
  clearValidationErrors,
  setDeclarationDirty,
  setStepsCompleted,
  deleteWorkflowSession,
  createWorkflowSession
} from '../../store/reducers';
import { AlertTriangle, TriangleAlert } from 'lucide-react';
import { useLanguage, getGeneratedSuccessfullyModalTranslation } from '../../hooks/useLanguage';
import { clearWorkflowSessionStorage } from '../../utils/workflowSessionStorage';
import { clearAllTabsFromSession } from '../../utils/tabStorage';

// Specific error message from API that requires a page refresh
const TARIC_REFRESH_ERROR_MESSAGE = "Ha ocurrido un error desconocido durante el proceso de importación. Por favor revise el log o contacte con soporte.";

export const GeneratedSuccessfullyModal = ({ 
  setIsSuccessfulModalOpen, 
  setIsProcessing, 
  setIsReviewConfirmed,
  hasError = false,
  errorMessage = "An error occurred while generating the Taric declaration.",
  onRetry
}) => {
  const dispatch = useDispatch();
  const [isSpanish] = useLanguage();
  const successfulExpedientes = useSelector((state) => state.preparedJson.successfulExpedientes || []);
  const errorExpedientes = useSelector((state) => state.preparedJson.errorExpedientes || []);
  
  // Get active workflow session ID to refresh only that workflow
  const activeWorkflowSessionId = useSelector((state) => state.workflowSessions?.activeSessionId);
  const workflowSessions = useSelector((state) => state.workflowSessions);
  
  // Check if the error message matches the specific Taric error that requires refresh
  const isTaricRefreshError = errorMessage === TARIC_REFRESH_ERROR_MESSAGE;
  
  // CRITICAL: Determine if this is actually an error state - hasError prop is true, OR - errorExpedientes exist, OR - no expedientes were created at all (false success scenario)
  const isActuallyError = hasError || 
                          (Array.isArray(errorExpedientes) && errorExpedientes.length > 0) || 
                          (Array.isArray(successfulExpedientes) && successfulExpedientes.length === 0 && 
                           Array.isArray(errorExpedientes) && errorExpedientes.length === 0);
  
  // Format expedientes list with commas
  const formatExpedientes = (expedientes) => {
    if (expedientes.length === 0) return '';
    return expedientes.join(', ');
  };

  const handleRefreshApp = () => {
    window.location.reload();
  };

  const handleClose = () => {
    // dispatch(resetDocumentsState()); dispatch(resetSelectedSender()); dispatch(resetSelectedReceiver()); dispatch(resetSelectedAgent()); setIsProcessing(false); setIsReviewConfirmed(false);
    setIsSuccessfulModalOpen(false);
  }

  const handleSuccessfulClose = async () => {
    // Only refresh workflow if generation was completely successful (no errors)
    const isCompletelySuccessful = !hasError && errorExpedientes.length === 0 && successfulExpedientes.length > 0;
    
    if (!isCompletelySuccessful) {
      // If there are errors, just close the modal without refreshing
      setIsSuccessfulModalOpen(false);
      return;
    }
    
    // For completely successful generation, refresh the active workflow only (no page reload)
    if (activeWorkflowSessionId) {
      // Extract workflow number from sessionId (e.g., "workflow-1" -> "1")
      const workflowNumber = activeWorkflowSessionId.replace('workflow-', '') || '1';
      const defaultName = `Workflow ${workflowNumber}`;
      
      // 1. Clear workflow-specific sessionStorage (includes lastJobId)
      clearWorkflowSessionStorage(activeWorkflowSessionId);
      
      // 2. Clear all tabs from Redux (tabs belong to workflows)
      dispatch(resetTabs());
      
      // 3. Clear all tab data from sessionStorage
      clearAllTabsFromSession();
      
      // 4. PDFs are now stored only in memory (workflow file cache) - no localStorage cleanup needed
      
      // 5. Clear global Redux state before deleting workflow
      dispatch(setExtractedData(null));
      dispatch(setEditableData(null));
      dispatch(clearValidationErrors());
      dispatch(setDeclarationDirty(false));
      dispatch(setStepsCompleted(false));
      dispatch(resetDocumentsState());
      
      // 6. Delete the current workflow session after successful generation IMPORTANT: If this is the only workflow, create a new empty one first This ensures there's always at least one workflow available
      const sessionOrder = workflowSessions?.sessionOrder || [];
      const isOnlyWorkflow = sessionOrder.length <= 1;
      
      if (isOnlyWorkflow) {
        // Create a new empty workflow before deleting the current one This ensures the user always has at least one workflow to work with
        const newSessionId = `workflow-${workflowSessions?.nextSessionIndex || 1}`;
        const newWorkflowName = `Workflow ${workflowSessions?.nextSessionIndex || 1}`;
        dispatch(createWorkflowSession(newSessionId, newWorkflowName));
      }
      
      // Delete the workflow that was just successfully generated
      dispatch(deleteWorkflowSession(activeWorkflowSessionId));
    }
    
    // 7. Clear global sessionStorage items (legacy, but keep for compatibility)
    sessionStorage.removeItem('hasCompletedJob');
    sessionStorage.removeItem('activeJobId');
    sessionStorage.removeItem('documentReadyReceived');
    sessionStorage.removeItem('lastJobId'); // Clear global lastJobId (legacy)
    
    // 8. Clear active job to stop the stream
    window.dispatchEvent(new CustomEvent('clearActiveJobId'));
    
    // 9. Close FieldOverview panel since workflow is reset and there's no data to show
    window.dispatchEvent(new CustomEvent('fieldOverviewCollapse', { 
      detail: { collapsed: true } 
    }));
    
    // 10. Close the modal (NO PAGE RELOAD - workflow is deleted and new one created if needed)
    if (setIsProcessing) setIsProcessing(false);
    if (setIsReviewConfirmed) setIsReviewConfirmed(false);
    setIsSuccessfulModalOpen(false);

    // 11. Scroll main content to top after modal closes
    setTimeout(() => {
      const container = document.getElementById('analysis-report');
      if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
    }, 150);
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-999 text-center">
      <div className="bg-white p-4 rounded-xl shadow-lg max-w-md w-full">
          <div className='flex justify-end'>
          {/* CRITICAL: Use handleClose (not handleSuccessfulClose) if this is actually an error state */}
          <img className='h-6 w-6 cursor-pointer' src={closeIcon} onClick={() => isActuallyError ? handleClose() : handleSuccessfulClose()} />
        </div>
        <div className="flex flex-col items-center mb-4">
          {/* Show icon based on whether there are errors */}
          {/* CRITICAL: Show error icon if this is actually an error state (including false success) */}
          {(isActuallyError && successfulExpedientes.length === 0) ? (
            <AlertTriangle className="p-4 h-24 w-24 mb-4 text-red-500 stroke-red-500" />
          ) : (
            <img className="h-24 w-24 mb-2" src={checkIcon} alt="Success" />
          )}

  {(errorExpedientes.length > 0 &&
   (successfulExpedientes.length > 0) && (
            <h2 className="text-xs font-light mb-1">{isSpanish ? "Exportaciones generadas con éxito" : "Exports successfully generated"}</h2>
          ))}

          {/* Display successful expedientes as big H1 */}
          {successfulExpedientes.length > 0 && (
            <h1 className="text-3xl font-bold mb-2 text-template-primary">
              {formatExpedientes(successfulExpedientes)}
            </h1>
          )}
          
          {/* Display error expedientes - large if no successful ones, small if there are successful ones */}
          {errorExpedientes.length > 0 && (
            successfulExpedientes.length === 0 ? (
              <h1 className="text-3xl font-bold mb-2 text-red-500">
                {formatExpedientes(errorExpedientes)}
              </h1>
            ) : (
              <div className="flex items-center gap-1 mt-4 flex-col mb-4">
                <h2 className="text-xs font-light">{isSpanish ? "Exportaciones con errores" : "Exports with errors"}</h2>
                <p className="text-xl font-semibold text-red-500">
                  {formatExpedientes(errorExpedientes)}
                </p>
              </div>
            )
          )}
          
          
          <h2 className="text-xl font-bold mb-2 mt-4">
            {/* CRITICAL: Show failure if this is actually an error state (including false success) */}
            {isActuallyError
              ? getGeneratedSuccessfullyModalTranslation('generationFailed', isSpanish) 
              : getGeneratedSuccessfullyModalTranslation('taricDeclarationSuccess', isSpanish)}
          </h2>
          
          {/* Show message based on results */}
          {/* CRITICAL: Show error message if this is actually an error state (including false success) */}
          {isActuallyError ? (
            <>
              <p className='opacity-75 text-sm mb-4'>{errorMessage}. {getGeneratedSuccessfullyModalTranslation('closeToCheckErrors', isSpanish)}</p>
              {/* Show refresh button only for specific Taric error message */}
              {isTaricRefreshError && (
                <button
                  onClick={handleRefreshApp}
                  className="mt-4 px-6 py-2 bg-default text-white rounded-full font-semibold hover:bg-opacity-90 transition-colors"
                >
                {isSpanish ? 'Refrescar la aplicación' : 'Refresh app'}
                </button>
              )}
            </>
          ) : (
            <p className='opacity-75 text-sm'>{getGeneratedSuccessfullyModalTranslation('yourDeclarationSaved', isSpanish)}</p>
          )}
        </div>
      </div>
    </div>
  );
};